import api from '../utils/api';
import authService from './authService';

const branchService = {
  getAllBranches: async (restaurantId = null) => {
    try {
      const currentUser = authService.getUserFromStorage();
      const params = restaurantId ? { restaurantId } : {};
      const response = await api.get('/branches', { params });
      let branches = Array.isArray(response.data) ? response.data : [];
      branches.forEach(branch => {
        if (branch._id && !branch.id) branch.id = branch._id;
      });
      return branches;
    } catch (error) {
      console.error('Error fetching branches:', error);
      return [];
    }
  },

  getBranchesByRestaurant: async (restaurantId) => {
    try {
      if (!restaurantId) {
        throw new Error('Restaurant ID is required');
      }

      const currentUser = authService.getUserFromStorage();
      const response = await api.get('/branches', { 
        params: { restaurantId: restaurantId }
      });

      let branches = response.data;

      // Process branch data
      branches = branches.map(branch => ({
        ...branch,
        id: branch._id || branch.id,
      }));

      // Filter branches based on user permissions
      if (currentUser && 
          currentUser.role !== 'owner' && 
          !currentUser.permissions?.manageRestaurants) {
        
        if (currentUser.branchPermissions?.menu || currentUser.branchPermissions?.tables) {
          const accessibleBranchIds = [
            ...(currentUser.branchPermissions?.menu || []),
            ...(currentUser.branchPermissions?.tables || [])
          ].map(id => String(id));
          
          branches = branches.filter(branch => 
            accessibleBranchIds.includes(String(branch.id))
          );
        } else if (currentUser.branchId) {
          branches = branches.filter(branch => 
            String(branch.id) === String(currentUser.branchId)
          );
        }
      }

      return branches;
    } catch (error) {
      console.error('Error fetching branches for restaurant:', error);
      return [];
    }
  },

  getBranchById: async (id) => {
    try {
      const branchId = String(id).trim(); 
      
      const currentUser = authService.getUserFromStorage();
      
      const response = await api.get(`/branches/${branchId}`);
      
      if (response.data && response.data._id) {
        response.data.id = response.data._id;
      }
      
      if (currentUser && 
          currentUser.role !== 'owner' && 
          !currentUser.permissions?.manageRestaurants && 
          !currentUser.permissions?.manageBranches) {
        
        const normalizedBranchId = String(branchId);
        
        const hasMenuAccess = currentUser.branchPermissions?.menu?.some(id => 
          String(id) === normalizedBranchId
        );
        
        const hasTableAccess = currentUser.branchPermissions?.tables?.some(id => 
          String(id) === normalizedBranchId
        );
        
        const isAssignedToBranch = String(currentUser.branchId) === normalizedBranchId;
        const isAssignedViaList = (
          currentUser.assigned_branch_ids ||
          currentUser.assignedBranchIds ||
          currentUser.branch_ids || []
        ).map(String).includes(normalizedBranchId);

        if (!hasMenuAccess && !hasTableAccess && !isAssignedToBranch && !isAssignedViaList) {
          console.warn("User doesn't have permission to access this branch");
        }
      }
      
      return response.data;
    } catch (error) {
      console.error(`Error fetching branch with ID ${id}:`, error);
      // Throw the error instead of returning a fallback object
      // This allows the calling code to handle the error properly
      throw error.response?.data || { message: 'Branch not found' };
    }
  },

  getRemotePrintStatus: async (branchId) => {
    try {
      const response = await api.get(`/branches/${String(branchId).trim()}/remote-print-status`);
      return response.data;
    } catch (error) {
      console.error('Error fetching remote print status:', error);
      throw error;
    }
  },

  getBranchRemotePrintManagers: async (branchId) => {
    try {
      const response = await api.get(`/branches/${String(branchId).trim()}/remote-print-managers`);
      const managers = Array.isArray(response.data?.managers) ? response.data.managers : [];
      managers.forEach((m) => {
        if (m._id && !m.id) m.id = m._id;
      });
      return {
        managers,
        remotePrintUserId: response.data?.remotePrintUserId || null,
        migrationRequired: response.data?.migrationRequired === true,
      };
    } catch (error) {
      console.error('Error fetching remote print managers:', error);
      throw error;
    }
  },

  getRestaurantById: async (id) => {
    try {
      const restaurantId = typeof id === 'object' ? id.toString() : id;
      const response = await api.get(`/restaurants/${restaurantId}`);
      
      if (response.data && response.data._id && !response.data.id) {
        response.data.id = response.data._id;
      }
      
      return response.data;
    } catch (error) {
      console.error(`Error fetching restaurant with ID ${id}:`, error);
      throw error;
    }
  },

  getManagers: async () => {
    try {
      const response = await api.get('/users?role=manager');
      
      if (Array.isArray(response.data)) {
        response.data.forEach(manager => {
          if (manager._id && !manager.id) {
            manager.id = manager._id;
          }
        });
      }
      
      return response.data;
    } catch (error) {
      console.error('Error fetching managers:', error);
      return [];
    }
  },

  createBranch: async (branchData, options = {}) => {
    try {
      const currentUser = authService.getUserFromStorage();
      
      if (currentUser && currentUser.role !== 'owner' && !currentUser.permissions?.manageBranches) {
        throw new Error("Access denied: You don't have permission to create branches");
      }
      
      // Process the data to handle null/empty values properly
      const processedData = { ...branchData };
      
      // Debug: Log what we receive from form
      console.log('=== SERVICE RECEIVED ===');
      console.log('managerId debug:', {
        value: processedData.managerId,
        type: typeof processedData.managerId,
        isNull: processedData.managerId === null,
        isUndefined: processedData.managerId === undefined,
        isStringNull: processedData.managerId === 'null',
        isEmpty: processedData.managerId === ''
      });
      
      // Handle managerId - remove if it's not a valid ObjectId
      if (!processedData.managerId || 
          processedData.managerId === '' || 
          processedData.managerId === 'null' || 
          processedData.managerId === null ||
          processedData.managerId === undefined) {
        console.log('Deleting managerId because it is empty/null/invalid:', processedData.managerId);
        delete processedData.managerId;
      }
      
      // Handle image/logo upload if present
      if (processedData.image || processedData.logo) {
        const formData = new FormData();
        
        Object.keys(processedData).forEach(key => {
          if ((key === 'image' || key === 'logo') && processedData[key]) {
            formData.append(key, processedData[key]);
          } else {
            formData.append(key, typeof processedData[key] === 'object' && processedData[key] !== null ? 
              JSON.stringify(processedData[key]) : processedData[key]);
          }
        });
        
        const response = await api.post('/branches', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: options.onUploadProgress
        });
        
        if (response.data && response.data._id) {
          response.data.id = response.data._id;
        }
        
        return response.data;
      } else {
        // No image/logo provided, send as regular JSON
        const response = await api.post('/branches', processedData);
        
        if (response.data && response.data._id) {
          response.data.id = response.data._id;
        }
        
        return response.data;
      }
    } catch (error) {
      console.error('Error creating branch:', error);
      throw error;
    }
  },

  updateBranch: async (id, branchData, options = {}) => {
    try {
      // Process the data to handle null/empty values properly
      const processedData = { ...branchData };

      // Handle managerId - convert empty string to null, don't send if null
      if (processedData.managerId === '' || processedData.managerId === 'null') {
        delete processedData.managerId;
      }

      // If a File object was provided for the logo, convert it to a base64 data-URL
      // and send it as `logoUrl` in a regular JSON body.
      // Sending multipart stores a local disk path (e.g. uploads/xyz.jpg) which
      // disappears whenever the server restarts — base64 is stored directly in the
      // DB column and therefore persists indefinitely.
      const logoFile =
        processedData.logo instanceof File ? processedData.logo :
        processedData.image instanceof File ? processedData.image :
        null;

      if (logoFile) {
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(logoFile);
        });

        // Replace the File object(s) with the base64 string
        delete processedData.logo;
        delete processedData.image;
        processedData.logoUrl = base64;
      }

      // Always send as JSON (logoUrl is now either base64 or an existing string/empty)
      const response = await api.put(`/branches/${id}`, processedData);

      if (response.data && response.data._id && !response.data.id) {
        response.data.id = response.data._id;
      }

      return response.data;
    } catch (error) {
      console.error('Error updating branch:', error);
      throw error;
    }
  },

  deleteBranch: async (branchId) => {
    try {
      const response = await api.delete(`/branches/${branchId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting branch:', error);
      throw error;
    }
  },
  
  // Analytics methods
  getBranchAnalytics: async (branchId, timeRange = '30d', filters = {}) => {
    try {
      // For the single-day view send the browser's LOCAL date so sessions that
      // started after midnight local time (but before midnight UTC) resolve to
      // the correct business day regardless of server timezone.
      const params = { timeRange };
      if (timeRange === '1d' || timeRange === 'yesterday') {
        const d = new Date();
        params.localDate = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      }
      if (filters?.startDate && filters?.endDate) {
        params.startDate = filters.startDate;
        params.endDate = filters.endDate;
      }
      const response = await api.get(`/branches/${branchId}/analytics`, { params });
      const data = response.data;
      return {
        totalSales: data.totalSales ?? 0,
        totalOrders: data.totalOrders ?? 0,
        totalCancelledOrders: data.totalCancelledOrders ?? 0,
        totalCancelledAmount: data.totalCancelledAmount ?? 0,
        totalExpenses: data.totalExpenses ?? 0,
        monthlyGrowth: 0,
        averageOrderValue: data.totalOrders > 0 ? Math.round((data.totalSales / data.totalOrders) * 100) / 100 : 0,
        performanceMetrics: { tableTurnoverRate: 0, averagePreparationTime: 0, peakHours: 'N/A' },
        topItems: data.topItems || [],
        salesByDay: data.salesByDay || [],
        ordersByDay: data.ordersByDay || [],
        expensesByDay: data.expensesByDay || [],
        cashSalesByDay: data.cashSalesByDay || [],
        creditSalesByDay: data.creditSalesByDay || [],
        cashExpensesByDay: data.cashExpensesByDay || [],
        creditExpensesByDay: data.creditExpensesByDay || [],
        startDate: data.startDate || filters?.startDate || null,
        endDate: data.endDate || filters?.endDate || null,
        timeRange: data.timeRange || timeRange,
        businessDate: data.businessDate || null,
      };
    } catch (error) {
      console.error('Error fetching branch analytics:', error);
      return {
        totalSales: 0,
        totalOrders: 0,
        totalCancelledOrders: 0,
        totalCancelledAmount: 0,
        totalExpenses: 0,
        monthlyGrowth: 0,
        averageOrderValue: 0,
        performanceMetrics: { tableTurnoverRate: 0, averagePreparationTime: 0, peakHours: 'N/A' },
        topItems: [],
        salesByDay: [],
        ordersByDay: [],
        expensesByDay: [],
        cashSalesByDay: [],
        creditSalesByDay: [],
        cashExpensesByDay: [],
        creditExpensesByDay: [],
      };
    }
  },

  _getBranchAnalyticsLegacy: async (branchId, timeRange = '30d', filters = {}) => {
    try {
      let orders = [];
      
      try {
        // First, try the new branch-specific orders endpoint for most accurate data
        try {
          const branchOrdersResponse = await api.get(`/branches/${branchId}/orders`, { 
            params: { 
              timeRange, 
              ...filters,
              limit: 1000 // Get more orders for accurate counting
            } 
          });
          
          if (branchOrdersResponse.data && branchOrdersResponse.data.orders) {
            orders = branchOrdersResponse.data.orders;
            console.log('Orders fetched from branch-specific endpoint:', orders.length);
          } else if (Array.isArray(branchOrdersResponse.data)) {
            orders = branchOrdersResponse.data;
            console.log('Orders fetched from branch-specific endpoint (array):', orders.length);
          }
        } catch (branchOrdersError) {
          console.log('Branch-specific orders endpoint not available, trying alternatives...');
        }
        
        // If branch-specific endpoint didn't work, try the general orders endpoint
        if (orders.length === 0) {
          const ordersResponse = await api.get('/orders', { 
            params: { 
              branchId, 
              timeRange, 
              ...filters,
              limit: 1000 // Get more orders for accurate counting
            } 
          });
          
          // Handle different response structures
          if (ordersResponse.data && ordersResponse.data.orders) {
            orders = ordersResponse.data.orders;
          } else if (Array.isArray(ordersResponse.data)) {
            orders = ordersResponse.data;
          } else {
            orders = [];
          }
          
          console.log('Orders fetched from /orders endpoint:', orders.length);
        }
        
        // If we still don't have enough orders, try the order history endpoint
        if (orders.length === 0 || orders.length < 100) {
          try {
            const historyResponse = await api.get('/orders/history', { 
              params: { 
                branchId, 
                timeRange, 
                ...filters,
                limit: 1000 
              } 
            });
            
            let historyOrders = [];
            if (historyResponse.data && historyResponse.data.orders) {
              historyOrders = historyResponse.data.orders;
            } else if (Array.isArray(historyResponse.data)) {
              historyOrders = historyResponse.data;
            }
            
            console.log('Orders fetched from /orders/history endpoint:', historyOrders.length);
            
            // Merge orders, avoiding duplicates
            const existingOrderIds = new Set(orders.map(order => order._id || order.id));
            const newOrders = historyOrders.filter(order => 
              !existingOrderIds.has(order._id || order.id)
            );
            
            orders = [...orders, ...newOrders];
            console.log('Total orders after merging:', orders.length);
          } catch (historyError) {
            console.log('Order history endpoint not available:', historyError.message);
          }
        }
        
        console.log('Final total orders fetched:', orders.length);
        if (orders.length > 0) {
          console.log('Sample order structure:', orders[0]);
        }
      } catch (ordersError) {
        console.log('All order endpoints failed, using fallback data:', ordersError.message);
        orders = [];
      }
      
      // Try to get menu items (currently not used in analytics but kept for future use)
      try {
        await api.get('/menu-items', { 
          params: { branchId, timeRange } 
        });
      } catch (menuItemsError) {
        console.log('Menu items endpoint not available');
      }

      // Try to get expenses for the branch
      let expenses = [];
      try {
        // First try to get expenses from the daily expense manager endpoint
        const dailyExpensesResponse = await api.get('/expenses/daily', { 
          params: { branchId, timeRange } 
        });
        
        if (dailyExpensesResponse.data && dailyExpensesResponse.data.data) {
          expenses = dailyExpensesResponse.data.data;
        } else if (Array.isArray(dailyExpensesResponse.data)) {
          expenses = dailyExpensesResponse.data;
        }
        
        console.log('Daily expenses fetched from expense manager:', expenses.length);
      } catch (dailyExpensesError) {
        console.log('Daily expenses endpoint not available, trying general expenses endpoint');
        
        // Fallback to general expenses endpoint
        try {
          const expensesResponse = await api.get('/expenses', { 
            params: { branchId, timeRange } 
          });
          
          if (expensesResponse.data && expensesResponse.data.data) {
            expenses = expensesResponse.data.data;
          } else if (Array.isArray(expensesResponse.data)) {
            expenses = expensesResponse.data;
          }
          
          console.log('Expenses fetched from general endpoint:', expenses.length);
        } catch (expensesError) {
          console.log('General expenses endpoint not available, trying POS service');
          
          // Try to get expenses from POS service (daily expense manager)
          try {
            const posExpensesResponse = await api.get('/pos/expenses', { 
              params: { branchId, timeRange } 
            });
            
            if (posExpensesResponse.data && posExpensesResponse.data.data) {
              expenses = posExpensesResponse.data.data;
            } else if (Array.isArray(posExpensesResponse.data)) {
              expenses = posExpensesResponse.data;
            }
            
            console.log('Expenses fetched from POS service:', expenses.length);
          } catch (posExpensesError) {
            console.log('POS expenses endpoint not available, using fallback data');
            expenses = [];
          }
        }
      }

      // If still no expenses, try to get from localStorage (daily expense manager data)
      if (expenses.length === 0) {
        console.log('No expenses from API, checking localStorage for daily expense manager data');
        try {
          const storedExpenses = localStorage.getItem('dailyExpenses');
          if (storedExpenses) {
            const parsedExpenses = JSON.parse(storedExpenses);
            // Filter expenses for the current branch and time range
            const branchExpenses = parsedExpenses.filter(expense => {
              if (expense.branchId && expense.branchId !== branchId) {
                return false;
              }
              
              const expenseDate = new Date(expense.date || expense.createdAt);
              const now = new Date(); // Use a new Date object for consistency
              const startDate = new Date(now.getTime() - (timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : timeRange === '90d' ? 90 : 365) * 24 * 60 * 60 * 1000);
              
              return expenseDate >= startDate && expenseDate <= now;
            });
            
            if (branchExpenses.length > 0) {
              expenses = branchExpenses;
              console.log('Expenses loaded from localStorage (daily expense manager):', expenses.length);
            }
          }
        } catch (localStorageError) {
          console.log('Error reading expenses from localStorage:', localStorageError);
        }
      }

      // Generate minimal mock data only if absolutely no expenses are available
      if (expenses.length === 0) {
        console.log('No expenses available from any source, generating minimal mock data for demonstration');
        const mockExpenseCategories = [
          'Food Supplies', 'Labor', 'Utilities', 'Rent', 'Equipment', 
          'Marketing', 'Insurance', 'Maintenance', 'Cleaning', 'Miscellaneous'
        ];
        
        // Generate only 1-2 expenses per day for realistic demonstration
        const now = new Date(); // Use a new Date object for consistency
        const startDate = new Date(now.getTime() - (timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : timeRange === '90d' ? 90 : 365) * 24 * 60 * 60 * 1000);
        const daysDiff = Math.ceil((now - startDate) / (1000 * 60 * 60 * 24));
        
        for (let i = 0; i < daysDiff; i++) {
          const date = new Date(startDate.getTime() + i * 24 * 60 * 60 * 1000);
          const dateStr = date.toISOString().split('T')[0];
          
          // Generate 1-2 expenses per day (more realistic)
          const numExpenses = Math.floor(Math.random() * 2) + 1;
          for (let j = 0; j < numExpenses; j++) {
            const category = mockExpenseCategories[Math.floor(Math.random() * mockExpenseCategories.length)];
            const amount = Math.random() * 150 + 30; // $30-$180 per expense (more realistic)
            
            expenses.push({
              date: dateStr,
              amount: Math.round(amount * 100) / 100,
              category: category,
              description: `${category} expense for ${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`,
              createdAt: date.toISOString(),
              branchId: branchId
            });
          }
        }
        
        console.log('Generated minimal mock expenses:', expenses.length);
      }

      // Apply time range filtering to orders
      const now = new Date();
      let startDate;
      
      switch (timeRange) {
        case '7d':
          startDate = new Date(now.getTime() - (7 * 24 * 60 * 60 * 1000));
          break;
        case '30d':
          startDate = new Date(now.getTime() - (30 * 24 * 60 * 60 * 1000));
          break;
        case '90d':
          startDate = new Date(now.getTime() - (90 * 24 * 60 * 60 * 1000));
          break;
        case '1y':
          startDate = new Date(now.getTime() - (365 * 24 * 60 * 60 * 1000));
          break;
        default:
          startDate = new Date(now.getTime() - (30 * 24 * 60 * 60 * 1000)); // Default to 30 days
      }

      // Filter orders by date range and ensure they belong to the branch
      const filteredOrders = orders.filter(order => {
        // Check if order belongs to this branch
        const orderBranchId = order.branchId || order.branch?.id || order.branch;
        if (orderBranchId && String(orderBranchId) !== String(branchId)) {
          return false;
        }
        
        // Check date range - handle multiple date field formats
        const orderDate = new Date(order.createdAt || order.orderDate || order.date || order.updatedAt);
        return orderDate >= startDate && orderDate <= now;
      });

      console.log('Time range:', timeRange);
      console.log('Start date:', startDate);
      console.log('Total orders before filtering:', orders.length);
      console.log('Total orders after filtering:', filteredOrders.length);
      console.log('Orders data:', filteredOrders);

      // Calculate analytics from filtered data - only orders and sales
      const totalSales = filteredOrders.reduce((sum, order) => {
        const orderTotal = order.totalAmount || order.total || order.grandTotal || 0;
        return sum + (typeof orderTotal === 'number' ? orderTotal : parseFloat(orderTotal) || 0);
      }, 0);
      
      // This is the key fix - ensure totalOrders is accurately counted from filtered orders
      const totalOrders = filteredOrders.length;
      
      console.log('Calculated totalOrders:', totalOrders);
      console.log('Calculated totalSales:', totalSales);
      
      // Calculate daily data for charts
      const dailyData = {};
      const dailyOrders = {};
      
      filteredOrders.forEach(order => {
        const orderDate = order.createdAt || order.orderDate || order.date || order.updatedAt;
        if (orderDate) {
          const date = new Date(orderDate).toISOString().split('T')[0];
          if (!dailyData[date]) {
            dailyData[date] = 0;
            dailyOrders[date] = 0;
          }
          const orderTotal = order.totalAmount || order.total || order.grandTotal || 0;
          dailyData[date] += (typeof orderTotal === 'number' ? orderTotal : parseFloat(orderTotal) || 0);
          dailyOrders[date] += 1;
        }
      });

      // Calculate daily expenses
      const dailyExpenses = {};
      expenses.forEach(expense => {
        const expenseDate = expense.date || expense.createdAt || expense.updatedAt;
        if (expenseDate) {
          const date = new Date(expenseDate).toISOString().split('T')[0];
          if (!dailyExpenses[date]) {
            dailyExpenses[date] = 0;
          }
          // Handle different expense amount field names
          const expenseAmount = expense.amount || expense.price || expense.total || expense.cost || 0;
          const parsedAmount = typeof expenseAmount === 'number' ? expenseAmount : parseFloat(expenseAmount) || 0;
          dailyExpenses[date] += parsedAmount;
        }
      });

      // Convert to arrays for charts
      const salesByDay = Object.entries(dailyData)
        .sort(([a], [b]) => new Date(a) - new Date(b))
        .map(([date, sales]) => ({ date, sales }));

      const ordersByDay = Object.entries(dailyOrders)
        .sort(([a], [b]) => new Date(a) - new Date(b))
        .map(([date, orders]) => ({ date, orders }));

      const expensesByDay = Object.entries(dailyExpenses)
        .sort(([a], [b]) => new Date(a) - new Date(b))
        .map(([date, expenses]) => ({ date, expenses }));

      // Calculate top performing items with better data extraction
      const itemStats = {};
      filteredOrders.forEach(order => {
        const orderItems = order.items || order.orderItems || [];
        if (Array.isArray(orderItems)) {
          orderItems.forEach(item => {
            const itemName = item.name || item.menuItemName || item.itemName || 'Unknown Item';
            const itemPrice = parseFloat(item.price || item.unitPrice || 0);
            const itemQuantity = parseInt(item.quantity || 1);
            
            if (!itemStats[itemName]) {
              itemStats[itemName] = { orders: 0, revenue: 0, quantity: 0 };
            }
            itemStats[itemName].orders += 1;
            itemStats[itemName].revenue += itemPrice * itemQuantity;
            itemStats[itemName].quantity += itemQuantity;
          });
        }
      });

      const topItems = Object.entries(itemStats)
        .map(([name, stats]) => ({ name, ...stats }))
        .sort((a, b) => b.revenue - a.revenue)
        .slice(0, 5);

      // Calculate performance metrics with better data extraction
      const completedOrders = filteredOrders.filter(order => {
        const status = order.status || order.orderStatus || '';
        return status.toLowerCase() === 'completed' || status.toLowerCase() === 'delivered';
      });
      
      console.log('Completed orders for prep time calculation:', completedOrders.length);
      
      let averagePreparationTime = 0;
      let totalPreparationTime = 0;
      let validPreparationOrders = 0;
      
      // Enhanced preparation time calculation
      completedOrders.forEach(order => {
        const createdAt = order.createdAt || order.orderDate || order.date;
        const completedAt = order.completedAt || order.deliveredAt || order.updatedAt || order.modifiedAt;
        
        if (createdAt && completedAt) {
          const startTime = new Date(createdAt);
          const endTime = new Date(completedAt);
          
          // Only calculate if the dates are valid and end time is after start time
          if (!isNaN(startTime.getTime()) && !isNaN(endTime.getTime()) && endTime > startTime) {
            const prepTimeMinutes = (endTime - startTime) / (1000 * 60);
            
            if (prepTimeMinutes > 0 && prepTimeMinutes < 1440) { // Less than 24 hours (reasonable prep time)
              totalPreparationTime += prepTimeMinutes;
              validPreparationOrders += 1;
            }
          }
        }
      });
      
      // If no valid preparation times found, try alternative approach
      if (validPreparationOrders === 0) {
        console.log('No valid preparation times found, trying alternative calculation...');
        
        // Try to calculate from order status changes
        filteredOrders.forEach(order => {
          const createdAt = order.createdAt || order.orderDate || order.date;
          const statusHistory = order.statusHistory || order.statusUpdates || [];
          
          if (createdAt && Array.isArray(statusHistory) && statusHistory.length > 0) {
            // Find when order was completed
            const completedStatus = statusHistory.find(update => 
              update.status && (update.status.toLowerCase() === 'completed' || update.status.toLowerCase() === 'delivered')
            );
            
            if (completedStatus && completedStatus.timestamp) {
              const startTime = new Date(createdAt);
              const endTime = new Date(completedStatus.timestamp);
              
              if (!isNaN(startTime.getTime()) && !isNaN(endTime.getTime()) && endTime > startTime) {
                const prepTimeMinutes = (endTime - startTime) / (1000 * 60);
                
                if (prepTimeMinutes > 0 && prepTimeMinutes < 1440) {
                  totalPreparationTime += prepTimeMinutes;
                  validPreparationOrders += 1;
                }
              }
            }
          }
        });
      }
      
      // Final fallback: estimate preparation time based on order complexity
      if (validPreparationOrders === 0 && filteredOrders.length > 0) {
        console.log('Using fallback preparation time estimation...');
        
        filteredOrders.forEach(order => {
          const orderItems = order.items || order.orderItems || [];
          let estimatedPrepTime = 15; // Base 15 minutes
          
          if (Array.isArray(orderItems) && orderItems.length > 0) {
            // Add time based on number of items and complexity
            estimatedPrepTime += orderItems.length * 3; // 3 minutes per item
            
            // Add time for special items (pizza, complex dishes)
            const hasComplexItems = orderItems.some(item => {
              const itemName = (item.name || item.menuItemName || '').toLowerCase();
              return itemName.includes('pizza') || itemName.includes('steak') || itemName.includes('pasta');
            });
            
            if (hasComplexItems) {
              estimatedPrepTime += 10; // Extra time for complex items
            }
          }
          
          totalPreparationTime += estimatedPrepTime;
          validPreparationOrders += 1;
        });
      }
      
      averagePreparationTime = validPreparationOrders > 0 ? totalPreparationTime / validPreparationOrders : 0;
      
      console.log('Preparation time calculation summary:', {
        totalCompletedOrders: completedOrders.length,
        validPreparationOrders,
        totalPreparationTime,
        averagePreparationTime,
        allOrders: filteredOrders.length
      });

      // Calculate table turnover rate (if table data is available)
      let tableTurnoverRate = 0;
      const tableOrders = filteredOrders.filter(order => order.tableId || order.table);
      if (tableOrders.length > 0) {
        // Simple calculation: orders per day / number of tables
        const uniqueTables = new Set(tableOrders.map(order => order.tableId || order.table)).size;
        const daysWithData = Object.keys(dailyOrders).length || 1;
        tableTurnoverRate = uniqueTables > 0 ? (tableOrders.length / daysWithData) / uniqueTables : 0;
      }

      // Calculate peak hours if time data is available
      let peakHours = 'N/A';
      const hourlyOrders = {};
      filteredOrders.forEach(order => {
        const orderDate = order.createdAt || order.orderDate || order.date;
        if (orderDate) {
          const hour = new Date(orderDate).getHours();
          hourlyOrders[hour] = (hourlyOrders[hour] || 0) + 1;
        }
      });
      
      if (Object.keys(hourlyOrders).length > 0) {
        const peakHour = Object.entries(hourlyOrders).reduce((a, b) => hourlyOrders[a[0]] > hourlyOrders[b[0]] ? a : b)[0];
        const peakHourFormatted = `${peakHour}:00`;
        peakHours = peakHourFormatted;
      }

      const analytics = {
        totalSales: Math.round(totalSales * 100) / 100, // Round to 2 decimal places
        totalOrders, // This now accurately reflects the filtered orders count
        monthlyGrowth: 0, // This would need historical data comparison
        averageOrderValue: totalOrders > 0 ? Math.round((totalSales / totalOrders) * 100) / 100 : 0,
        performanceMetrics: {
          tableTurnoverRate: Math.round(tableTurnoverRate * 100) / 100,
          averagePreparationTime: Math.round(averagePreparationTime),
          peakHours
        },
        topItems,
        salesByDay,
        ordersByDay,
        expensesByDay
      };

      console.log('Final calculated analytics:', analytics);
      console.log('Key metrics verification:');
      console.log('- totalOrders:', analytics.totalOrders);
      console.log('- ordersByDay total:', analytics.ordersByDay.reduce((sum, day) => sum + day.orders, 0));
      console.log('- totalSales:', analytics.totalSales);
      console.log('- salesByDay total:', analytics.salesByDay.reduce((sum, day) => sum + day.sales, 0));

      return analytics;
    } catch (error) {
      console.error('Error fetching branch analytics:', error);
      
      // Return fallback data structure if API fails
      return {
        totalSales: 0,
        totalOrders: 0,
        monthlyGrowth: 0,
        averageOrderValue: 0,
        performanceMetrics: {
          tableTurnoverRate: 0,
          averagePreparationTime: 0,
          peakHours: 'N/A'
        },
        topItems: [],
        salesByDay: [],
        ordersByDay: [],
        expensesByDay: []
      };
    }
  },

  getBranchOrders: async (branchId, timeRange = '30d', filters = {}) => {
    try {
      const params = { timeRange, ...filters };
      const response = await api.get(`/branches/${branchId}/orders`, { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching branch orders:', error);
      return [];
    }
  },

  getBranchMenuItems: async (branchId, timeRange = '30d') => {
    try {
      const params = { timeRange };
      const response = await api.get(`/branches/${branchId}/menu-items`, { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching branch menu items:', error);
      return [];
    }
  },

  getBranchStats: async (branchId) => {
    try {
      const response = await api.get(`/branches/${String(branchId).trim()}/stats`);
      return {
        tableCount: response.data?.tableCount ?? 0,
        availableTables: response.data?.availableTables ?? 0,
        occupiedTables: response.data?.occupiedTables ?? 0,
        reservedTables: response.data?.reservedTables ?? 0,
        menuItemCount: response.data?.menuItemCount ?? 0,
        staffCount: response.data?.staffCount ?? 0,
      };
    } catch (error) {
      console.error('Error fetching branch stats:', error);
      return {
        tableCount: 0,
        availableTables: 0,
        occupiedTables: 0,
        reservedTables: 0,
        menuItemCount: 0,
        staffCount: 0,
      };
    }
  },
};

export default branchService;